import React, { useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import HierarchyVirtualList from './Components/HierarchyVirtualList';
import { useHierarchyData } from './HierarchyHooks/useHierarchyData';
import { useHierarchyState } from './HierarchyHooks/useHierarchyState';
import { useHierarchySearch } from './HierarchyHooks/useHierarchySearch';
import { useHierarchyDragDrop } from './HierarchyHooks/useHierarchyDragDrop';
import { SearchBox } from '../SearchBox';
import { filterTreeByTypes } from './helpers';
import './_style.scss';

const HierarchyView = React.memo(({
    nodes,
    nodeMap,
    isflattenData = true,
    selectedIds,
    expandedIds,
    onSelect,
    onExpand,
    onCollapse,
    onDrop,
    onNodeClick,
    renderNode,
    renderIcon,
    renderActions,
    searchEnabled,
    searchPlaceholder,
    filterFunction,
    multiSelect,
    dragEnabled,
    dropEnabled,
    height,
    itemHeight,
    overscanCount,
    indent,
    className,
    emptyMessage,
    showExpandIcon,
    showCheckbox,
    getNodeKey,
    isNodeSelectable,
    canDrop,
    onContextMenu,
    translate,
    externalNode,
    // Edit mode props
    editMode,
    hiddenNodeTypes,
    showDragHandle,
    externalNodeItems,
    allExternalNodeItems,
    isShowExternalNodes,
    selectedExternalIds,
    onSaveNewNode,
    onCancelNewNode,
}) => {
    // Manage expansion and selection state (initialize with props)
    const {
        localExpandedIds,
        localSelectedIds,
        handleToggleExpand,
        handleSelect: handleStateSelect,
        handleSelectRange,
        lastSelectedIndex,
        updateFlattenedNodes,
        editingNodeId,
    } = useHierarchyState({
        initialExpandedIds: expandedIds,
        initialSelectedIds: selectedIds,
        onExpand,
        onCollapse,
        onSelect,
        multiSelect,
        editMode,
    });

    // Filter out hidden node types (edit mode)
    const filteredNodes = useMemo(() => {
        if (editMode && hiddenNodeTypes && hiddenNodeTypes.length > 0) {
            return filterTreeByTypes(nodes, hiddenNodeTypes);
        }
        return nodes;
    }, [nodes, editMode, hiddenNodeTypes]);

    // Transform and flatten data for virtualization (using local state for expand/collapse)
    // Call hook unconditionally to preserve hook order; use its result conditionally.
    const { flattenedNodes: fn, nodeMap: nm } = useHierarchyData({
        nodes: filteredNodes,
        expandedIds: localExpandedIds,
        getNodeKey,
    });
    let flattenedNodes = filteredNodes;
    if (isflattenData) {
        flattenedNodes = fn;
        nodeMap = nm;
    }

    // Update flattened nodes in state hook for selection operations
    useEffect(() => {
        updateFlattenedNodes(flattenedNodes);
    }, [flattenedNodes, updateFlattenedNodes]);

    // Search and filter functionality
    const {
        searchTerm,
        setSearchTerm,
        filteredNodes: searchFilteredNodes,
    } = useHierarchySearch({
        nodes: flattenedNodes,
        nodeMap,
        filterFunction,
        localExpandedIds,
    });

    // Drag and drop functionality
    const {
        activeId,
        overId,
        isDragging,
        dropPosition,
        handleDragStart,
        handleDragOver,
        handleDragEnd,
        handleDragCancel,
        getDraggedItems,
        setDropPosition,
    } = useHierarchyDragDrop({
        enabled: dragEnabled && dropEnabled,
        selectedIds: localSelectedIds,
        nodeMap,
        onDrop,
        canDrop,
        externalNodes: externalNodeItems || [],
        allExternalNodes: allExternalNodeItems || externalNodeItems || [],
        selectedExternalIds: selectedExternalIds || [],
    });

    // Handle node click with selection logic
    const handleNodeClickInternal = useCallback((node, event) => {
        const index = searchFilteredNodes.findIndex(n => getNodeKey(n) === getNodeKey(node));

        if (multiSelect) {
            if (event.ctrlKey || event.metaKey) {
                // Toggle selection
                handleStateSelect(node, true);
            } else if (event.shiftKey && lastSelectedIndex !== null) {
                // Range selection
                handleSelectRange(index);
            } else {
                // Single selection (clear others)
                handleStateSelect(node, false);
            }
        } else {
            handleStateSelect(node, false);
        }

        if (onNodeClick) {
            onNodeClick(node, event);
        }
    }, [searchFilteredNodes, getNodeKey, multiSelect, handleStateSelect, handleSelectRange, lastSelectedIndex, onNodeClick]);

    // Handle context menu
    const handleContextMenuInternal = useCallback((node, event) => {
        event.preventDefault();
        if (onContextMenu) {
            onContextMenu(node, event);
        }
    }, [onContextMenu]);

    // Handle search change
    const handleSearchChange = useCallback((value) => {
        setSearchTerm(value);
    }, [setSearchTerm]);

    // Render empty state
    const renderEmptyState = useMemo(() => {
        if (!searchFilteredNodes || searchFilteredNodes.length === 0) {
            const message = searchTerm
                ? translate ? translate('ABB.Powertrain.Frontend.noSearchResults') : 'No results found'
                : emptyMessage || (translate ? translate('ABB.Powertrain.Frontend.noData') : 'No data available');

            return (
                <div className='hierarchy-view-empty'>
                    {message}
                </div>
            );
        }
        return null;
    }, [searchFilteredNodes, searchTerm, emptyMessage, translate]);

    // Get dragged items for preview
    const draggedItems = useMemo(() => {
        if (isDragging && activeId) {
            return getDraggedItems();
        }
        return [];
    }, [isDragging, activeId, getDraggedItems]);

    // Container classes
    const containerClasses = useMemo(() => {
        const classes = ['hierarchy-view-container'];
        if (className) { classes.push(className); }
        if (editMode) { classes.push('hierarchy-view-edit-mode'); }
        return classes.join(' ');
    }, [className, editMode]);

    return (
        <div className={containerClasses}>
            {/* Search bar */}
            {searchEnabled &&
                <div className='hierarchy-view-search'>
                    <SearchBox
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder={searchPlaceholder || ''}
                    />
                </div>
            }

            {/* Virtualized list */}
            {renderEmptyState ||
                <HierarchyVirtualList
                    nodes={searchFilteredNodes}
                    selectedIds={localSelectedIds}
                    expandedIds={localExpandedIds}
                    height={height}
                    itemHeight={itemHeight}
                    overscanCount={overscanCount}
                    indent={indent}
                    onNodeClick={handleNodeClickInternal}
                    onToggleExpand={handleToggleExpand}
                    onContextMenu={handleContextMenuInternal}
                    renderNode={renderNode}
                    renderIcon={renderIcon}
                    renderActions={renderActions}
                    showExpandIcon={showExpandIcon}
                    showCheckbox={showCheckbox}
                    getNodeKey={getNodeKey}
                    isNodeSelectable={isNodeSelectable}
                    dragEnabled={dragEnabled}
                    dropEnabled={dropEnabled}
                    activeId={activeId}
                    overId={overId}
                    dropPosition={dropPosition}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                    translate={translate}
                    externalNode={externalNode}
                    // Edit mode props
                    editMode={editMode}
                    editingNodeId={editingNodeId}
                    showDragHandle={showDragHandle}
                    externalNodeItems={externalNodeItems}
                    isShowExternalNodes={isShowExternalNodes}
                    isDragging={isDragging}
                    draggedItems={draggedItems}
                    onSaveNewNode={onSaveNewNode}
                    onCancelNewNode={onCancelNewNode}
                    setDropPosition={setDropPosition}
                />
            }
        </div>
    );
});

HierarchyView.propTypes = {
    /** Array of hierarchy nodes */
    nodes: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
        parentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        children: PropTypes.array,
        hasChildren: PropTypes.bool,
        data: PropTypes.object,
        type: PropTypes.string,
        selectable: PropTypes.bool,
        draggable: PropTypes.bool,
        disabled: PropTypes.bool,
    })).isRequired,
    /** Node map for O(1) lookups */
    nodeMap: PropTypes.object,
    /** Whether to flatten tree data for virtualization */
    isflattenData: PropTypes.bool,
    /** Array of selected node IDs (controlled) */
    selectedIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    /** Array of expanded node IDs (controlled) */
    expandedIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    /** Callback when selection changes: (selectedNodes, selectedIds) => void */
    onSelect: PropTypes.func,
    /** Callback when node is expanded: (node) => void */
    onExpand: PropTypes.func,
    /** Callback when node is collapsed: (node) => void */
    onCollapse: PropTypes.func,
    /** Callback when drag and drop completes: (draggedNodes, targetNode, position) => void */
    onDrop: PropTypes.func,
    /** Callback when node is clicked: (node, event) => void */
    onNodeClick: PropTypes.func,
    /** Custom node renderer: (node, { isSelected, isExpanded, level }) => ReactNode */
    renderNode: PropTypes.func,
    /** Custom icon renderer: (node, { isExpanded }) => ReactNode */
    renderIcon: PropTypes.func,
    /** Custom actions renderer: (node) => ReactNode */
    renderActions: PropTypes.func,
    /** Enable search functionality */
    searchEnabled: PropTypes.bool,
    /** Search input placeholder text */
    searchPlaceholder: PropTypes.string,
    /** Custom filter function: (node, searchTerm) => boolean */
    filterFunction: PropTypes.func,
    /** Enable multi-select */
    multiSelect: PropTypes.bool,
    /** Enable drag functionality */
    dragEnabled: PropTypes.bool,
    /** Enable drop functionality */
    dropEnabled: PropTypes.bool,
    /** Container height in pixels */
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** Height of each item in pixels */
    itemHeight: PropTypes.number,
    /** Number of items to render outside visible area */
    overscanCount: PropTypes.number,
    /** Indentation per level in pixels */
    indent: PropTypes.number,
    /** Additional CSS class name */
    className: PropTypes.string,
    /** Message to display when no data */
    emptyMessage: PropTypes.string,
    /** Show expand/collapse icons */
    showExpandIcon: PropTypes.bool,
    /** Show selection checkboxes */
    showCheckbox: PropTypes.bool,
    /** Function to get unique key from node: (node) => string|number */
    getNodeKey: PropTypes.func,
    /** Function to determine if node is selectable: (node) => boolean */
    isNodeSelectable: PropTypes.func,
    /** Function to validate drop: (draggedNodes, targetNode, position) => boolean */
    canDrop: PropTypes.func,
    /** Callback for context menu: (node, event) => void */
    onContextMenu: PropTypes.func,
    /** Translation function */
    translate: PropTypes.func,
    /** External node element to render alongside the list */
    externalNode: PropTypes.node,
    /** Enable edit mode for add/rename/delete operations */
    editMode: PropTypes.bool,
    /** Array of node types to hide from view */
    hiddenNodeTypes: PropTypes.arrayOf(PropTypes.string),
    /** Show drag handle icon for draggable nodes */
    showDragHandle: PropTypes.bool,
    /** Flattened external node items for DnD context */
    externalNodeItems: PropTypes.array,
    /** Full (unfiltered) external node items used to resolve dragged selections */
    allExternalNodeItems: PropTypes.array,
    /** Whether to show the external nodes panel */
    isShowExternalNodes: PropTypes.bool,
    /** Selected external node IDs */
    selectedExternalIds: PropTypes.array,
    /** Callback to save a new node: (name, node, setError) => void */
    onSaveNewNode: PropTypes.func,
    /** Callback to cancel adding a new node */
    onCancelNewNode: PropTypes.func,
};

HierarchyView.defaultProps = {
    nodeMap: null,
    isflattenData: true,
    selectedIds: [],
    expandedIds: [],
    onSelect: null,
    onExpand: null,
    onCollapse: null,
    onDrop: null,
    onNodeClick: null,
    renderNode: null,
    renderIcon: null,
    renderActions: null,
    searchEnabled: false,
    searchPlaceholder: '',
    filterFunction: null,
    multiSelect: false,
    dragEnabled: false,
    dropEnabled: false,
    height: 600,
    itemHeight: 40,
    overscanCount: 5,
    indent: 20,
    className: '',
    emptyMessage: '',
    showExpandIcon: true,
    showCheckbox: false,
    getNodeKey: (node) => node.id,
    isNodeSelectable: (node) => node.selectable !== false && !node.disabled,
    canDrop: () => true,
    onContextMenu: null,
    translate: null,
    externalNode: null,
    editMode: false,
    hiddenNodeTypes: [],
    showDragHandle: false,
    externalNodeItems: [],
    allExternalNodeItems: [],
    isShowExternalNodes: false,
    selectedExternalIds: [],
    onSaveNewNode: null,
    onCancelNewNode: null,
};

HierarchyView.displayName = 'HierarchyView';

// eslint-disable-next-line import/no-unused-modules
export default HierarchyView;
